import { useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useUnitSchedule, useUnitScopeItems, useBillingCycles, useCreateBillingCycle } from "@/hooks/useUnitSchedule";
import { useProjectContracts, useContractSovLines } from "@/hooks/useProjectContracts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Upload, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { UnitImportWizard } from "./UnitImportWizard";
import { UnitAssignmentPanel } from "./UnitAssignmentPanel";
import { format, addMonths } from "date-fns";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);

// Deterministic color palette for vendor badges (HSL-based design tokens mapped to Tailwind-safe classes)
const VENDOR_COLORS = [
  { bg: "bg-blue-100 dark:bg-blue-900/40", text: "text-blue-700 dark:text-blue-300", ring: "ring-blue-400" },
  { bg: "bg-emerald-100 dark:bg-emerald-900/40", text: "text-emerald-700 dark:text-emerald-300", ring: "ring-emerald-400" },
  { bg: "bg-amber-100 dark:bg-amber-900/40", text: "text-amber-700 dark:text-amber-300", ring: "ring-amber-400" },
  { bg: "bg-purple-100 dark:bg-purple-900/40", text: "text-purple-700 dark:text-purple-300", ring: "ring-purple-400" },
  { bg: "bg-rose-100 dark:bg-rose-900/40", text: "text-rose-700 dark:text-rose-300", ring: "ring-rose-400" },
  { bg: "bg-cyan-100 dark:bg-cyan-900/40", text: "text-cyan-700 dark:text-cyan-300", ring: "ring-cyan-400" },
  { bg: "bg-orange-100 dark:bg-orange-900/40", text: "text-orange-700 dark:text-orange-300", ring: "ring-orange-400" },
  { bg: "bg-indigo-100 dark:bg-indigo-900/40", text: "text-indigo-700 dark:text-indigo-300", ring: "ring-indigo-400" },
];

function getInitials(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getVendorColor(vendorId: string) {
  let hash = 0;
  for (let i = 0; i < vendorId.length; i++) {
    hash = ((hash << 5) - hash + vendorId.charCodeAt(i)) | 0;
  }
  return VENDOR_COLORS[Math.abs(hash) % VENDOR_COLORS.length];
}

// Small badge component for sub initials
function SubBadge({ vendorId, vendorName, greyed }: { vendorId: string; vendorName: string; greyed: boolean }) {
  const initials = getInitials(vendorName);
  const color = getVendorColor(vendorId);

  if (greyed) {
    return (
      <span
        className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full text-[9px] font-bold bg-muted text-muted-foreground opacity-50"
        title={vendorName}
      >
        {initials}
      </span>
    );
  }

  return (
    <span
      className={cn("inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full text-[9px] font-bold", color.bg, color.text)}
      title={vendorName}
    >
      {initials}
    </span>
  );
}

interface Props {
  projectId: string;
  project: any;
  onMutate: () => void;
}

export const ProjectUnitsTab = ({ projectId, project, onMutate }: Props) => {
  const { can } = usePermissions();
  const { user } = useAuth();
  const { data: units, refetch: refetchUnits } = useUnitSchedule(projectId);
  const { data: scopeItems, refetch: refetchScope } = useUnitScopeItems(projectId);
  
  const { data: contracts } = useProjectContracts(projectId);
  const contract = contracts?.[0];
  const { data: sovLines } = useContractSovLines(contract?.id);
  const { data: cycles, refetch: refetchCycles } = useBillingCycles(projectId);
  const createCycle = useCreateBillingCycle();

  // Fetch work_order_lines with vendor info — this is the real source of assignments
  const { data: woLines, refetch: refetchWoLines } = useQuery({
    queryKey: ["wo_lines_for_units", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("work_order_lines")
        .select("unit_id, sov_line_id, status, billed_amount, completed_qty, work_order_id, work_orders!inner(contractor_id, project_id, vendors!inner(id, name))")
        .eq("work_orders.project_id", projectId!)
        .not("unit_id", "is", null);
      if (error) throw error;
      return data as any[];
    },
    enabled: !!user && !!projectId,
  });

  const [importOpen, setImportOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSovLineId, setAssignSovLineId] = useState<string | null>(null);
  const [cycleDialogOpen, setCycleDialogOpen] = useState(false);
  const [newCycleName, setNewCycleName] = useState("");
  const [newCycleCutoff, setNewCycleCutoff] = useState("");

  // Auto-suggest cycle name/date
  const suggestCycle = () => {
    const now = new Date();
    const nextMonth = addMonths(now, 0);
    const name = format(nextMonth, "MMMM yyyy");
    const cutoffDay = (project as any)?.billing_cutoff_day ?? 25;
    const cutoffDate = format(new Date(nextMonth.getFullYear(), nextMonth.getMonth(), Math.min(cutoffDay, 28)), "yyyy-MM-dd");
    setNewCycleName(name);
    setNewCycleCutoff(cutoffDate);
    setCycleDialogOpen(true);
  };

  const handleCreateCycle = async () => {
    if (!newCycleName.trim() || !newCycleCutoff) return;
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");
      await createCycle.mutateAsync({
        companyId,
        projectId,
        cycleName: newCycleName.trim(),
        cycleNumber: (cycles?.length ?? 0) + 1,
        cutoffDate: newCycleCutoff,
      });
      toast.success(`Billing cycle "${newCycleName}" created`);
      setCycleDialogOpen(false);
      refetchCycles();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  // Build the grid data
  const gridData = useMemo(() => {
    if (!units?.length || !sovLines?.length) return null;

    const scopeMap = new Map<string, any>();
    (scopeItems ?? []).forEach((si: any) => {
      scopeMap.set(`${si.unit_id}__${si.sov_line_id}`, si);
    });

    // Build assignment + completion map from work_order_lines
    // Key: unit_id__sov_line_id → { vendor_id, vendor_name, isBilled, isComplete }
    const woLineMap = new Map<string, { vendor_id: string; vendor_name: string; isBilled: boolean; isComplete: boolean }>();
    (woLines ?? []).forEach((wol: any) => {
      if (!wol.unit_id || !wol.sov_line_id) return;
      const key = `${wol.unit_id}__${wol.sov_line_id}`;
      const vendor = wol.work_orders?.vendors;
      const isBilled = wol.status === "billed" || Number(wol.billed_amount) > 0;
      const isComplete = wol.status === "complete" || wol.status === "billed" || Number(wol.completed_qty) > 0;
      const existing = woLineMap.get(key);
      if (existing) {
        existing.isBilled = existing.isBilled || isBilled;
        existing.isComplete = existing.isComplete || isComplete;
      } else {
        woLineMap.set(key, {
          vendor_id: vendor?.id ?? "",
          vendor_name: vendor?.name ?? "",
          isBilled,
          isComplete,
        });
      }
    });

    const sovTotals: Record<string, number> = {};
    const sovMismatches: string[] = [];
    sovLines.forEach((sl: any) => {
      const total = (scopeItems ?? [])
        .filter((si: any) => si.sov_line_id === sl.id && si.has_scope)
        .reduce((s: number, si: any) => s + Number(si.quantity), 0);
      sovTotals[sl.id] = total;
      if (Math.abs(total - Number(sl.quantity)) > 0.01) {
        sovMismatches.push(sl.id);
      }
    });

    // Collect unique vendors for legend
    const vendorLegend = new Map<string, { id: string; name: string }>();
    woLineMap.forEach((v) => {
      if (v.vendor_id && v.vendor_name) {
        vendorLegend.set(v.vendor_id, { id: v.vendor_id, name: v.vendor_name });
      }
    });

    return { scopeMap, woLineMap, sovTotals, sovMismatches, vendorLegend };
  }, [units, sovLines, scopeItems, woLines]);

  const handleRefresh = () => {
    refetchUnits();
    refetchScope();
    refetchCycles();
    refetchWoLines();
    onMutate();
  };

  const handleAssign = (sovLineId: string) => {
    setAssignSovLineId(sovLineId);
    setAssignOpen(true);
  };

  if (!contract || !sovLines?.length) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Create a contract with SOV lines first to set up units.</p>
      </div>
    );
  }

  if (!units?.length) {
    return (
      <div className="text-center py-12 space-y-4">
        <div className="text-4xl">📊</div>
        <h3 className="text-lg font-semibold">No units imported yet</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          Import your unit schedule from Excel to start tracking contractor assignments and billing.
        </p>
        <Button onClick={() => setImportOpen(true)} size="lg">
          <Upload className="mr-2 h-4 w-4" /> Import from Excel →
        </Button>
        <UnitImportWizard
          open={importOpen}
          onOpenChange={setImportOpen}
          projectId={projectId}
          sovLines={sovLines}
          onComplete={handleRefresh}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {units.length} units | {sovLines.length} SOV lines
            {gridData && gridData.sovMismatches.length > 0 && (
              <span className="text-warning ml-2">
                | ⚠️ {gridData.sovMismatches.length} lines not balancing
              </span>
            )}
          </span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
            <RefreshCw className="mr-1 h-4 w-4" /> Re-import / Update
          </Button>
        </div>
      </div>

      {/* Grid */}
      <Card>
        <ScrollArea className="w-full">
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="sticky left-0 bg-muted/50 px-3 py-2 text-left font-medium z-10">Unit</th>
                  <th className="px-2 py-2 text-left font-medium">Floor</th>
                  {sovLines.map((sl: any) => (
                    <th
                      key={sl.id}
                      className="px-2 py-2 text-right font-medium min-w-[80px] cursor-pointer hover:bg-muted/80"
                      onClick={() => handleAssign(sl.id)}
                      title={`Click to assign: ${sl.description}`}
                    >
                      <div className="truncate max-w-[100px]">{sl.description}</div>
                      <div className="text-[10px] text-muted-foreground font-normal">{sl.unit}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {units.map((unit: any) => (
                  <tr key={unit.id} className="border-b hover:bg-muted/30">
                    <td className="sticky left-0 bg-background px-3 py-1.5 font-mono font-medium z-10">
                      {unit.unit_number}
                    </td>
                    <td className="px-2 py-1.5 text-muted-foreground">{unit.floor_number || "—"}</td>
                    {sovLines.map((sl: any) => {
                      const si = gridData?.scopeMap.get(`${unit.id}__${sl.id}`);
                      const qty = si?.has_scope ? Number(si.quantity) : 0;
                      const cellKey = `${unit.id}__${sl.id}`;
                      const wolInfo = gridData?.woLineMap.get(cellKey);
                      const isClosed = wolInfo?.isBilled || wolInfo?.isComplete;

                      const vendorId = wolInfo?.vendor_id;
                      const vendorName = wolInfo?.vendor_name;
                      const isAssigned = !!wolInfo;

                      // Determine cell styling
                      let cellClass = "text-muted-foreground/30";
                      if (qty > 0 && isAssigned && isClosed) {
                        cellClass = "text-muted-foreground/40 bg-muted/20";
                      } else if (qty > 0 && isAssigned) {
                        cellClass = "text-primary bg-primary/5";
                      } else if (qty > 0) {
                        cellClass = "text-warning bg-warning/5";
                      }

                      return (
                        <td
                          key={sl.id}
                          className={cn("px-2 py-1.5 text-right font-mono", cellClass)}
                        >
                          {qty > 0 ? (
                            <div className="flex items-center justify-end gap-1">
                              <span className={isClosed ? "opacity-40" : ""}>{fmt(qty)}</span>
                              {vendorId && vendorName && (
                                <SubBadge vendorId={vendorId} vendorName={vendorName} greyed={!!isClosed} />
                              )}
                            </div>
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {/* SOV Total row */}
                <tr className="border-t-2 bg-muted/30 font-semibold">
                  <td className="sticky left-0 bg-muted/30 px-3 py-1.5 z-10">SOV TOTAL</td>
                  <td></td>
                  {sovLines.map((sl: any) => {
                    const total = gridData?.sovTotals[sl.id] ?? 0;
                    return (
                      <td key={sl.id} className="px-2 py-1.5 text-right font-mono">
                        {fmt(total)}
                      </td>
                    );
                  })}
                </tr>
                <tr className="bg-muted/30 font-semibold">
                  <td className="sticky left-0 bg-muted/30 px-3 py-1.5 z-10">SOV LINE</td>
                  <td></td>
                  {sovLines.map((sl: any) => (
                    <td key={sl.id} className="px-2 py-1.5 text-right font-mono">
                      {fmt(Number(sl.quantity))}
                    </td>
                  ))}
                </tr>
                <tr className="bg-muted/30">
                  <td className="sticky left-0 bg-muted/30 px-3 py-1.5 font-semibold z-10">DIFF</td>
                  <td></td>
                  {sovLines.map((sl: any) => {
                    const total = gridData?.sovTotals[sl.id] ?? 0;
                    const diff = total - Number(sl.quantity);
                    const matches = Math.abs(diff) < 0.01;
                    return (
                      <td key={sl.id} className={cn("px-2 py-1.5 text-right font-mono text-xs", matches ? "text-primary" : "text-destructive")}>
                        {matches ? "0 ✅" : `${diff > 0 ? "+" : ""}${fmt(diff)} ⚠️`}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </ScrollArea>
      </Card>

      {/* Vendor Legend */}
      {gridData?.vendorLegend && gridData.vendorLegend.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-1">
          <span className="text-xs text-muted-foreground font-medium">Subs:</span>
          {Array.from(gridData.vendorLegend.values()).map((v) => (
            <div key={v.id} className="flex items-center gap-1">
              <SubBadge vendorId={v.id} vendorName={v.name} greyed={false} />
              <span className="text-xs text-muted-foreground">{v.name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Billing Cycles Section */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            <span>Billing Cycles</span>
            {can("edit_billing_cycles") && (
              <Button variant="outline" size="sm" onClick={suggestCycle}>
                <Plus className="mr-1 h-4 w-4" /> New Cycle
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!cycles?.length ? (
            <p className="text-sm text-muted-foreground py-2">No billing cycles yet. Create one to start tracking unit completions.</p>
          ) : (
            <div className="space-y-1">
              {cycles.map((c: any) => (
                <div key={c.id} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0">
                  <span className="font-medium">{c.cycle_name}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-muted-foreground">Cutoff: {c.cutoff_date}</span>
                    <Badge variant={c.status === "open" ? "default" : c.status === "finalized" ? "secondary" : "outline"}>
                      {c.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import Wizard */}
      <UnitImportWizard
        open={importOpen}
        onOpenChange={setImportOpen}
        projectId={projectId}
        sovLines={sovLines}
        onComplete={handleRefresh}
      />

      {/* Assignment Panel */}
      {assignSovLineId && (
        <UnitAssignmentPanel
          open={assignOpen}
          onOpenChange={setAssignOpen}
          projectId={projectId}
          sovLineId={assignSovLineId}
          sovLines={sovLines}
          units={units ?? []}
          scopeItems={scopeItems ?? []}
          assignments={woLines ?? []}
          onComplete={handleRefresh}
        />
      )}

      {/* New Cycle Dialog */}
      <Dialog open={cycleDialogOpen} onOpenChange={setCycleDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>New Billing Cycle</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Cycle Name</Label>
              <Input value={newCycleName} onChange={e => setNewCycleName(e.target.value)} placeholder="March 2026" />
            </div>
            <div className="space-y-1">
              <Label>Cutoff Date</Label>
              <Input type="date" value={newCycleCutoff} onChange={e => setNewCycleCutoff(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCycleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCycle} disabled={!newCycleName.trim() || !newCycleCutoff}>
              Create Cycle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
